import React, { useCallback, useRef, useState } from 'react';
import CotizacionDiaDialog from '../components/sistema/CotizacionDiaDialog';
import movMonedaService from '../services/movMonedaService';
import { normalizarFechaCotizacion } from '../utils/cotizacionDia';

type PendingResolve = {
  resolve: (value: Record<number, number>) => void;
  reject: (reason?: unknown) => void;
};

/**
 * Verifica cotizaciones faltantes del día y, si hace falta, muestra un diálogo bloqueante
 * para registrarlas con POST /api/MovMoneda/del-dia.
 */
export function useCotizacionDiaGuard() {
  const [open, setOpen] = useState(false);
  const [codigos, setCodigos] = useState<number[]>([]);
  const [fecha, setFecha] = useState(normalizarFechaCotizacion(new Date()));
  const pendingRef = useRef<PendingResolve | null>(null);
  const inFlightKeyRef = useRef<string | null>(null);
  /** Monedas ya con cotización del día (o recién registradas) en esta sesión: no volver a pedir. */
  const okDelDiaRef = useRef<Set<string>>(new Set());

  const marcaOk = useCallback((fechaNorm: string, codigosOk: number[]) => {
    codigosOk.forEach((c) => okDelDiaRef.current.add(`${fechaNorm}:${c}`));
  }, []);

  const closePending = useCallback((result: 'ok' | 'cancel', value?: Record<number, number>) => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    inFlightKeyRef.current = null;
    const fechaActual = fecha;
    setOpen(false);
    setCodigos([]);
    if (!pending) return;
    if (result === 'ok') {
      const regs = value || {};
      marcaOk(fechaActual, Object.keys(regs).map((k) => Number(k)).filter((n) => n > 0));
      pending.resolve(regs);
    } else {
      pending.reject(new Error('Cotización del día cancelada por el usuario'));
    }
  }, [fecha, marcaOk]);

  const asegurarCotizacionesDelDia = useCallback(
    async (
      codigosMoneda: number[],
      fechaDocumento?: string | Date | null,
    ): Promise<Record<number, number>> => {
      const fechaNorm = normalizarFechaCotizacion(fechaDocumento);
      const codigosUnicos = Array.from(
        new Set(
          (codigosMoneda || [])
            .map((c) => Number(c))
            .filter((c) => Number.isFinite(c) && c > 0),
        ),
      );
      if (!codigosUnicos.length) return {};

      const pendientes = codigosUnicos.filter((c) => !okDelDiaRef.current.has(`${fechaNorm}:${c}`));
      if (!pendientes.length) return {};

      const key = `${fechaNorm}:${pendientes.slice().sort((a, b) => a - b).join(',')}`;
      if (inFlightKeyRef.current === key && pendingRef.current) {
        return new Promise<Record<number, number>>((resolve, reject) => {
          const prev = pendingRef.current!;
          pendingRef.current = {
            resolve: (v) => {
              prev.resolve(v);
              resolve(v);
            },
            reject: (e) => {
              prev.reject(e);
              reject(e);
            },
          };
        });
      }

      let requeridas: number[] = [];
      try {
        const result = await movMonedaService.getCotizacionesRequeridas(pendientes, fechaNorm);
        requeridas = result.codigosMonedaSinCotizacion || [];
      } catch (error) {
        console.error('Error al consultar cotizaciones requeridas del día:', error);
        return {};
      }

      // Las que el backend ya tiene para el día: no volver a pedir en esta sesión.
      const yaCubiertas = pendientes.filter((c) => !requeridas.includes(c));
      if (yaCubiertas.length) marcaOk(fechaNorm, yaCubiertas);

      if (!requeridas.length) return {};

      inFlightKeyRef.current = key;
      setFecha(fechaNorm);
      setCodigos(requeridas);
      setOpen(true);

      return new Promise<Record<number, number>>((resolve, reject) => {
        pendingRef.current = { resolve, reject };
      });
    },
    [marcaOk],
  );

  const cotizacionDiaDialog = (
    <CotizacionDiaDialog
      open={open}
      codigosMoneda={codigos}
      fecha={fecha}
      onRegistradas={(regs) => closePending('ok', regs)}
      onCancel={() => closePending('cancel')}
    />
  );

  return {
    asegurarCotizacionesDelDia,
    cotizacionDiaDialog,
  };
}

export default useCotizacionDiaGuard;
